import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Star, Award, Coins, Shield, Sparkles, X } from 'lucide-react';

const REWARD_ICONS = {
  bonus_xp: { Icon: Star, color: 'text-amber-400', bg: 'bg-amber-500/20', label: 'Bonus XP' },
  bonus_coins: { Icon: Coins, color: 'text-yellow-400', bg: 'bg-yellow-500/20', label: 'Bonus Coins' },
  mystery_badge: { Icon: Award, color: 'text-purple-400', bg: 'bg-purple-500/20', label: 'Mystery Badge' },
  streak_shield: { Icon: Shield, color: 'text-emerald-400', bg: 'bg-emerald-500/20', label: 'Streak Shield' },
};

const Particle = ({ delay, x, size, color }) => (
  <div
    className="absolute rounded-full animate-particle pointer-events-none"
    style={{
      left: `${x}%`,
      bottom: '50%',
      width: size,
      height: size,
      backgroundColor: color,
      animationDelay: `${delay}ms`,
    }}
  />
);

const SurpriseRewardModal = ({ reward, onClose }) => {
  const [phase, setPhase] = useState('enter');
  const timerRef = useRef(null);

  useEffect(() => {
    requestAnimationFrame(() => setPhase('visible'));
    timerRef.current = setTimeout(() => {
      setPhase('exit');
      setTimeout(onClose, 400);
    }, 5000);
    return () => clearTimeout(timerRef.current);
  }, [onClose]);

  if (!reward) return null;

  const config = REWARD_ICONS[reward.surpriseType] || REWARD_ICONS.bonus_xp;
  const { Icon, color, bg } = config;

  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    delay: Math.random() * 800,
    x: 10 + Math.random() * 80,
    size: `${4 + Math.random() * 8}px`,
    color: ['#fbbf24', '#f59e0b', '#a855f7', '#10b981', '#f472b6', '#60a5fa'][Math.floor(Math.random() * 6)],
  }));

  return (
    <div className={`fixed inset-0 z-[10000] flex items-center justify-center transition-all duration-400 ${
      phase === 'enter' ? 'opacity-0' : phase === 'visible' ? 'opacity-100' : 'opacity-0'
    }`} data-testid="surprise-reward-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className={`relative z-10 w-[340px] overflow-hidden rounded-2xl border border-white/10 bg-gray-900 shadow-2xl transition-all duration-500 ${
        phase === 'visible' ? 'scale-100 translate-y-0' : 'scale-75 translate-y-8'
      }`}>
        {/* Particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {particles.map(p => <Particle key={p.id} {...p} />)}
        </div>

        {/* Close button */}
        <button onClick={onClose} className="absolute top-3 right-3 z-20 p-1 rounded-full text-gray-500 hover:text-white hover:bg-white/10 transition-colors">
          <X className="w-4 h-4" />
        </button>

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center p-8 text-center">
          {/* Sparkle header */}
          <div className="flex items-center gap-1 mb-4">
            <Sparkles className="w-4 h-4 text-amber-400 animate-spin-slow" />
            <span className="text-xs font-bold uppercase tracking-widest text-amber-400/80">Surprise Reward</span>
            <Sparkles className="w-4 h-4 text-amber-400 animate-spin-slow" style={{ animationDirection: 'reverse' }} />
          </div>

          {/* Icon */}
          <div className={`w-20 h-20 rounded-2xl ${bg} flex items-center justify-center mb-4 animate-bounce-in`}>
            <Icon className={`w-10 h-10 ${color}`} />
          </div>

          {/* Description */}
          <p className="text-lg font-bold text-white mb-1">
            {reward.description || 'You got a surprise reward!'}
          </p>

          {/* Amount */}
          {reward.amount && (
            <p className={`text-3xl font-black ${color} tabular-nums animate-count-up`}>
              +{reward.amount}
            </p>
          )}

          {/* Badge details */}
          {reward.badge && (
            <div className="mt-2 px-3 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <p className="text-sm font-semibold text-purple-300">{reward.badge.name}</p>
              <p className="text-xs text-purple-400/70 capitalize">{reward.badge.tier} tier</p>
            </div>
          )}

          {/* Streak Shield */}
          {reward.surpriseType === 'streak_shield' && (
            <p className="text-sm text-emerald-400/80 mt-2">Your streak is protected for 1 day!</p>
          )}
        </div>
      </div>
    </div>
  );
};

export const SurpriseRewardContainer = () => {
  const [reward, setReward] = useState(null);

  const showReward = useCallback((data) => {
    setReward(data);
  }, []);

  useEffect(() => {
    window.__surpriseReward = showReward;
    return () => { window.__surpriseReward = null; };
  }, [showReward]);

  if (!reward) return null;

  return <SurpriseRewardModal reward={reward} onClose={() => setReward(null)} />;
};

export default SurpriseRewardContainer;
